import React, { useState } from 'react';
import {
  MdClose,
  MdLightbulb,
  MdWarning,
  MdError,
  MdCheckCircle,
  MdExpandMore,
  MdExpandLess,
  MdRefresh
} from 'react-icons/md';

const AIFeedbackPane = ({
  showFeedback,
  onToggleFeedback,
  placement = 'bottom',
  feedbackItems = [],
  onRefreshFeedback,
  loading = false
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const getFeedbackIcon = (type) => {
    switch (type) {
      case 'suggestion':
        return <MdLightbulb className="text-blue-500" />;
      case 'warning':
        return <MdWarning className="text-yellow-500" />;
      case 'error':
        return <MdError className="text-red-500" />;
      case 'success':
        return <MdCheckCircle className="text-green-500" />;
      default:
        return <MdLightbulb className="text-blue-500" />;
    }
  };

  const getFeedbackTypeLabel = (type) => {
    switch (type) {
      case 'suggestion':
        return 'Suggestion';
      case 'warning':
        return 'Improvement';
      case 'error':
        return 'Issue';
      case 'success':
        return 'Good Practice';
      default:
        return 'Feedback';
    }
  };

  if (!showFeedback) return null;

  return (
    <div className={`bg-white border-t border-gray-200 flex flex-col h-full overflow-hidden ${
      placement === 'right' ? 'border-l border-t-0' : 'border-t'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <MdLightbulb className="text-blue-500 text-lg" />
          <h3 className="font-medium text-gray-900">AI Feedback</h3>
          {feedbackItems.length > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
              {feedbackItems.length}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onRefreshFeedback}
            disabled={loading}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
            title="Refresh AI feedback"
          >
            <MdRefresh className={`text-sm ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onToggleFeedback}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
            title="Close feedback panel"
          >
            <MdClose className="text-sm" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2 text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className="text-sm">AI is analyzing your code...</span>
            </div>
          </div>
        ) : feedbackItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MdLightbulb className="mx-auto text-3xl mb-2 text-gray-300" />
            <p className="text-sm">No feedback available yet.</p>
            <p className="text-xs mt-1">Start coding to receive AI guidance!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {feedbackItems.map((item) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-3 bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getFeedbackIcon(item.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {getFeedbackTypeLabel(item.type)}
                        </span>
                        <span className="text-xs text-gray-500">
                          Line {item.line}
                        </span>
                      </div>
                      {item.details && (
                        <button
                          onClick={() => toggleExpanded(item.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {expandedItems.has(item.id) ? (
                            <MdExpandLess className="text-sm" />
                          ) : (
                            <MdExpandMore className="text-sm" />
                          )}
                        </button>
                      )}
                    </div>

                    <p className="text-sm text-gray-800 mt-1 leading-relaxed">
                      {item.message}
                    </p>

                    {item.code && (
                      <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono text-gray-700 overflow-x-auto">
                        {item.code}
                      </div>
                    )}

                    {item.details && expandedItems.has(item.id) && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-200">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {item.details}
                        </p>
                        {item.example && (
                          <div className="mt-2">
                            <span className="text-xs font-medium text-gray-600 block mb-1">
                              Example:
                            </span>
                            <pre className="text-xs bg-white p-2 rounded border font-mono overflow-x-auto">
                              {item.example}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIFeedbackPane;